import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, CreditCard, Trash2, Clock, Store, Zap, ArrowLeft, Ticket, X } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { readSource } from "@/lib/source";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { StoreButton } from "@/components/Buttons";
import { BagArt } from "@/components/cart/Bag";
import { useGang } from "@/lib/useGang";
import { bestGangTier, cartGangLines, honorsGang, lineSavings } from "@/lib/gang";
import { HOUSE_SLUG, themeOf, tint } from "@/lib/mall";

const inputCls = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white placeholder:text-zinc-500 focus:border-white/60 focus:outline-none";
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/** Checkout for one shop. Each shop is paid separately — items from other shops stay in the cart. */
export default function Checkout() {
    const { store: storeSlug } = useParams();
    const [sp, setSp] = useSearchParams();
    const { bags, removeItem, clearStore } = useCart();
    const { user } = useAuth();
    const settings = useSettings();
    const { tiers } = useGang();
    const b = settings?.business;
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [mem, setMem] = useState(null);
    const [shop, setShop] = useState(null);
    const [code, setCode] = useState("");
    const [coupon, setCoupon] = useState(null);
    const [applying, setApplying] = useState(false);

    const bag = useMemo(() => bags.find((x) => x.slug === storeSlug), [bags, storeSlug]);
    const others = bags.filter((x) => x.slug !== storeSlug);
    const items = bag?.items || [];
    const subtotal = bag?.subtotal || 0;
    const theme = bag?.theme || themeOf(null);

    useEffect(() => {
        if (!storeSlug) return;
        api.get(`/stores/${storeSlug}`).then(({ data }) => setShop(data.store)).catch(() => setShop(null));
    }, [storeSlug]);

    // Back from Stripe without paying: the cart is untouched, the unannounced order is cancelled quietly.
    const cancelledId = sp.get("cancelled");
    useEffect(() => {
        if (!cancelledId) return;
        api.post(`/orders/${cancelledId}/abandon`).catch(() => { });
        sessionStorage.removeItem("pending_pay");
        toast.info("No charge was made. Your cart is still here.");
        setSp({}, { replace: true });
    }, [cancelledId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!user) { setMem(null); return; }
        api.get("/membership/me").then(({ data }) => {
            const m = data.membership;
            setMem(m && ["trial", "active"].includes(m.status) ? { ...m, ...data.tier } : null);
        }).catch(() => { });
    }, [user]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const bulk = (() => {
        let pct = 0;
        (b?.bulk_discounts || []).forEach((t) => { if (subtotal >= t.min) pct = t.pct; });
        return { pct, discount: subtotal * pct / 100 };
    })();

    const isColored = (it) => it.selections?.category === "colored" || /-colored$/.test(it.product_slug || "");
    const isWhiteApparel = (it) => it.selections?.category === "white" || /-white$/.test(it.product_slug || "")
        || /^new-white-shirt/.test(it.product_slug || "");
    const hasColored = items.some(isColored);
    const hasWhite = storeSlug === HOUSE_SLUG && items.some(isWhiteApparel);

    const gangHonored = honorsGang(items[0]?.store) || storeSlug === HOUSE_SLUG;
    const memberDiscount = mem && gangHonored ? lineSavings(mem, cartGangLines(items)) : 0;
    const best = bestGangTier(tiers, cartGangLines(items));
    const afterDiscounts = Math.max(subtotal - bulk.discount - memberDiscount, 0);
    const total = Math.max(afterDiscounts - (coupon?.discount || 0), 0);
    const canPayCard = Boolean(shop?.accepts_cards || storeSlug === HOUSE_SLUG) && total >= 0.5;

    const applyCoupon = async (e) => {
        e?.preventDefault();
        if (!code.trim()) return;
        setApplying(true);
        try {
            const { data } = await api.post("/coupons/apply", {
                code: code.trim(), store_slug: storeSlug, subtotal: afterDiscounts,
            });
            setCoupon({ ...data.coupon, discount: data.discount });
            toast.success(`${data.coupon.code} applied — ${data.coupon.title}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setApplying(false); }
    };

    const submit = async (payNow = false) => {
        if (!form.name || !form.email || !form.phone) { toast.error("Name, email and phone are required"); return; }
        setBusy(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_slug: i.product_slug, product_name: i.product_name, selections: i.selections, artwork: i.artwork || [], proof: i.proof || null, summary: i.summary || "" })),
                contact: form, notes, coupon_code: coupon?.code || "", source: readSource(), pay_now: payNow,
            };
            const { data } = await api.post("/orders", payload);
            if (payNow && (data.card_total || 0) >= 0.5) {
                // the cart is only emptied once Stripe confirms the payment (see PaymentResult)
                sessionStorage.setItem("pending_pay", JSON.stringify({ slug: storeSlug, order_id: data.id }));
                const origin = window.location.origin;
                const res = await api.post("/payments/order/checkout", {
                    order_id: data.id, origin_url: origin, cancel_url: `${origin}/checkout/${storeSlug}?cancelled=${data.id}`,
                });
                window.location.href = res.data.checkout_url;
                return;   // stay busy until the browser leaves for Stripe
            }
            clearStore(storeSlug);
            toast.success(`Order placed with ${bag.name}.`);
            navigate(`/order-confirmation/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setBusy(false);
        }
    };

    if (!bag) {
        return (
            <Layout hideTicker>
                <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center">
                    <ShoppingBag size={44} className="text-zinc-700" />
                    <h1 className="mt-4 font-display text-2xl font-bold">Nothing from this shop in your cart</h1>
                    <p className="mt-2 text-zinc-400">Your cart has no items from this shop right now.</p>
                    <Link to="/cart" className="mt-5 rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-black" data-testid="checkout-back-to-bags">
                        Back to cart
                    </Link>
                </div>
            </Layout>
        );
    }

    return (
        <Layout hideTicker>
            <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                <Link to="/cart" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-white" data-testid="checkout-bags-link">
                    <ArrowLeft size={13} /> back to cart
                </Link>

                <div className="mt-4 flex flex-wrap items-center gap-4">
                    <span className="shrink-0"><BagArt tier={bag.tier} theme={theme} size={64} /></span>
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.24em]" style={{ color: theme.ink || theme.primary }}>
                            Checkout · {bag.count} item{bag.count === 1 ? "" : "s"}
                        </p>
                        <h1 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="checkout-title">
                            Check out with {bag.name}
                        </h1>
                    </div>
                </div>

                {others.length > 0 && (
                    <div className="mt-5 flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="checkout-other-bags">
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">Also in your cart</span>
                        {others.map((o) => (
                            <Link key={o.slug} to={`/checkout/${o.slug}`} data-testid={`other-bag-${o.slug}`}
                                className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] transition-transform hover:scale-[1.03]"
                                style={{ borderColor: tint(o.theme.primary, 0.4), color: o.theme.ink || o.theme.primary }}>
                                <Store size={11} /> {o.name} · {money(o.subtotal)}
                            </Link>
                        ))}
                        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">— each shop is paid separately</span>
                    </div>
                )}

                {(hasColored || hasWhite) && (
                    <div className="mt-6 space-y-3">
                        {hasColored && (
                            <div className="flex items-start gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/[0.07] px-4 py-3.5" data-testid="colored-turnaround-notice">
                                <Clock size={17} className="mt-0.5 flex-shrink-0 text-amber-400" />
                                <p className="text-sm text-amber-100">
                                    <span className="font-bold">Colored apparel: allow up to 4 weeks.</span>{" "}
                                    Colored garments are ordered in for your job, so these items are not same-day.
                                </p>
                            </div>
                        )}
                        {hasWhite && (
                            <div className="flex items-start gap-3 rounded-2xl border border-brand-green/40 bg-brand-green/[0.07] px-4 py-3.5" data-testid="white-rush-notice">
                                <Zap size={17} className="mt-0.5 flex-shrink-0 text-brand-green" />
                                <p className="text-sm text-zinc-200">
                                    <span className="font-bold text-brand-green">White apparel is rushed today.</span>{" "}
                                    Order before 2:00 PM and your white items are printed and ready for pickup tonight.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-8 grid gap-6 lg:grid-cols-12">
                    <div className="space-y-6 lg:col-span-7">
                        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                            <h2 className="mb-4 font-display text-xl font-bold">Your details</h2>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <input className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="checkout-name" />
                                <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" />
                                <input className={`${inputCls} sm:col-span-2`} placeholder="Email" value={form.email} onChange={set("email")} data-testid="checkout-email" />
                                <textarea className={`${inputCls} sm:col-span-2`} rows={3} placeholder="Order notes (date needed, special instructions)…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes" />
                            </div>
                        </div>

                        <div className="rounded-3xl border p-6" data-testid="checkout-fulfillment"
                            style={{ borderColor: tint(theme.primary, 0.3), background: tint(theme.primary, 0.04) }}>
                            <h2 className="mb-1 font-display text-xl font-bold" style={{ color: theme.ink || theme.primary }}>{bag.name}</h2>
                            <p className="text-sm text-zinc-400">This shop's own pickup, turnaround and payment rules.</p>
                            <div className="mt-4 space-y-1 text-sm text-zinc-300">
                                {shop?.turnaround && <p><span className="text-zinc-500">Turnaround:</span> {shop.turnaround}</p>}
                                {shop?.hours && <p><span className="text-zinc-500">Hours:</span> {shop.hours}</p>}
                                {shop?.pickup_address && <p><span className="text-zinc-500">Pickup:</span> {shop.pickup_address}</p>}
                                {shop?.pickup_instructions && <p className="text-zinc-400">{shop.pickup_instructions}</p>}
                                {shop?.policies && <p className="text-zinc-400">{shop.policies}</p>}
                                {!shop?.turnaround && !shop?.pickup_address && <p className="text-zinc-400">This shop will message you with pickup and timing.</p>}
                                <p className="pt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                                    {canPayCard ? "Pay here by card, or by app / cash on pickup" : "Takes payment directly — they'll reach out"}
                                </p>
                                {canPayCard && (
                                    <p data-testid="checkout-address-note" className="text-[12px] text-zinc-400">
                                        Card checkout asks for a shipping address even on pickup orders. We keep it on file as a backup in case the order isn't picked up.
                                    </p>
                                )}
                            </div>
                        </div>

                        {canPayCard && (
                            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                                <h2 className="mb-3 font-display text-xl font-bold">How payment works</h2>
                                <p className="text-sm text-zinc-400">{b?.payment_note}</p>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    {(b?.payment_methods || []).map((m) => (
                                        <div key={m.type} className="rounded-xl border border-white/10 bg-brand-ink px-4 py-3">
                                            <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: theme.ink || theme.primary }}>{m.type}</div>
                                            <div className="font-semibold">{m.handle}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-24 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                            <h2 className="mb-4 font-display text-xl font-bold">Your items</h2>
                            <div className="space-y-3">
                                {items.map((it) => (
                                    <div key={it.id} className="flex gap-3" data-testid="checkout-item">
                                        <img src={mediaUrl(it.image)} alt="" className="h-14 w-14 flex-shrink-0 rounded-lg object-cover" onError={(e) => (e.target.style.visibility = "hidden")} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-semibold">{it.product_name}</p>
                                            <p className="truncate text-xs text-zinc-400">{it.summary}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-sm" style={{ color: theme.ink || theme.primary }}>{money(it.line_total)}</div>
                                            <button onClick={() => removeItem(it.id)} className="text-zinc-600 hover:text-red-400" data-testid="checkout-remove"><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="my-4 h-px bg-white/10" />
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="font-mono text-white">{money(subtotal)}</span></div>
                                {bulk.pct > 0 && (
                                    <div className="flex justify-between" style={{ color: theme.ink || theme.primary }}>
                                        <span>Bulk discount ({bulk.pct}%)</span><span className="font-mono">−{money(bulk.discount)}</span>
                                    </div>
                                )}
                                {mem && gangHonored && (
                                    <div className="flex justify-between" style={{ color: theme.ink || theme.primary }} data-testid="member-discount-line">
                                        <span>Gang {mem.tier_name} member</span><span className="font-mono">−{money(memberDiscount)}</span>
                                    </div>
                                )}
                                {!mem && (
                                    <Link to="/wholesale" className="flex justify-between text-zinc-500 hover:text-white" data-testid="join-gang-upsell">
                                        <span>{best && best.saved >= 0.5
                                            ? `Join the Gang — ${best.tier.name} saves you ${money(best.saved)} here`
                                            : "Join the wholesale club — save up to 15% + DTF rates"}</span>
                                        <span className="font-mono">→</span>
                                    </Link>
                                )}
                                {mem && !gangHonored && (
                                    <p className="pt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500" data-testid="gang-not-honored-note">
                                        {bag.name} doesn't honor Gang discounts — no member price at this shop
                                    </p>
                                )}
                                {coupon ? (
                                    <div className="flex items-center justify-between" style={{ color: theme.ink || theme.primary }} data-testid="coupon-discount-line">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Ticket size={13} /> {coupon.code}
                                            <button onClick={() => { setCoupon(null); setCode(""); }} data-testid="coupon-clear"
                                                className="text-zinc-500 hover:text-red-400"><X size={12} /></button>
                                        </span>
                                        <span className="font-mono">−{money(coupon.discount)}</span>
                                    </div>
                                ) : (
                                    <form onSubmit={applyCoupon} className="flex gap-2 pt-2" data-testid="coupon-form">
                                        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code"
                                            className="min-w-0 flex-1 rounded-full border border-white/15 bg-brand-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white placeholder:text-zinc-500 focus:border-white/60 focus:outline-none"
                                            data-testid="coupon-input" />
                                        <button type="submit" disabled={applying} data-testid="coupon-apply"
                                            className="rounded-full border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-200 transition-colors hover:border-white/50 hover:text-white disabled:opacity-50">
                                            {applying ? <Loader2 className="animate-spin" size={12} /> : "Apply"}
                                        </button>
                                    </form>
                                )}
                                <div className="flex justify-between pt-1 font-display text-lg font-bold">
                                    <span>Estimated total</span><span style={{ color: theme.ink || theme.primary }}>{money(total)}</span>
                                </div>
                                <Link to="/wholesale" data-testid="coupon-book-link"
                                    className="block pt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 hover:text-white">
                                    see the wholesale coupon book →
                                </Link>
                            </div>

                            {canPayCard ? (
                                <>
                                    <StoreButton theme={theme} className="mt-5 w-full" onClick={() => submit(true)} disabled={busy} data-testid="pay-now-button">
                                        {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                                        Pay {money(total)} now by card
                                    </StoreButton>
                                    <button onClick={() => submit(false)} disabled={busy}
                                        className="mt-3 w-full rounded-full border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-white/45 hover:text-white disabled:opacity-50"
                                        data-testid="place-order-button">
                                        Place order — pay later by app or cash
                                    </button>
                                </>
                            ) : (
                                <StoreButton theme={theme} className="mt-5 w-full" onClick={() => submit(false)} disabled={busy} data-testid="place-order-button">
                                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Store size={16} />} Place order with {bag.name}
                                </StoreButton>
                            )}

                            <p className="mt-3 text-[11px] text-zinc-500" data-testid="checkout-scope-note">
                                {others.length > 0
                                    ? `This pays ${bag.name} only. Your items from ${others.length === 1 ? "the other shop" : `${others.length} other shops`} stay in your cart until you check out with them.`
                                    : canPayCard
                                        ? "Paying now moves your job straight into production. Nothing leaves your cart until the payment goes through."
                                        : `${bag.name} takes payment directly — place the order and they'll contact you.`}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
